/**
 * 상담 기록 서비스
 * Counseling Record Service
 */

import { API_BASE_URL } from '../../../config/api';
import {
  CounselingRecord,
  CounselingRecordCreate,
  CounselingRecordUpdate,
  CounselingSummary,
} from '../models/CounselingRecord';

class CounselingRecordService {
  private baseUrl = `${API_BASE_URL}/v1/counseling-records`;

  /**
   * API 요청 헤더 생성
   */
  private getHeaders(): HeadersInit {
    const token = localStorage.getItem('accessToken');
    return {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
    };
  }

  /**
   * 상담 기록 생성
   */
  async createCounselingRecord(
    data: CounselingRecordCreate
  ): Promise<CounselingRecord> {
    const response = await fetch(this.baseUrl, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || '상담 기록 생성에 실패했습니다.');
    }

    return response.json();
  }

  /**
   * 상담 기록 단건 조회
   */
  async getCounselingRecord(recordId: string): Promise<CounselingRecord> {
    const response = await fetch(`${this.baseUrl}/${recordId}`, {
      method: 'GET',
      headers: this.getHeaders(),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || '상담 기록 조회에 실패했습니다.');
    }

    return response.json();
  }

  /**
   * 학생의 상담 기록 목록 조회
   */
  async getStudentCounselingRecords(
    studentId: string,
    limit: number = 10,
    offset: number = 0
  ): Promise<CounselingRecord[]> {
    const url = `${this.baseUrl}/student/${studentId}?limit=${limit}&offset=${offset}`;
    const response = await fetch(url, {
      method: 'GET',
      headers: this.getHeaders(),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || '상담 기록 조회에 실패했습니다.');
    }

    return response.json();
  }

  /**
   * 상담사의 상담 기록 목록 조회
   */
  async getCounselorCounselingRecords(
    counselorId: string,
    limit: number = 10,
    offset: number = 0
  ): Promise<CounselingRecord[]> {
    const url = `${this.baseUrl}/counselor/${counselorId}?limit=${limit}&offset=${offset}`;
    const response = await fetch(url, {
      method: 'GET',
      headers: this.getHeaders(),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || '상담 기록 조회에 실패했습니다.');
    }

    return response.json();
  }

  /**
   * 조직의 상담 기록 목록 조회
   */
  async getOrganizationCounselingRecords(
    organizationId: string,
    limit: number = 20,
    offset: number = 0
  ): Promise<CounselingRecord[]> {
    const url = `${this.baseUrl}/organization/${organizationId}?limit=${limit}&offset=${offset}`;
    const response = await fetch(url, {
      method: 'GET',
      headers: this.getHeaders(),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || '상담 기록 조회에 실패했습니다.');
    }

    return response.json();
  }

  /**
   * 학생의 상담 요약 정보 조회
   */
  async getStudentCounselingSummary(
    studentId: string
  ): Promise<CounselingSummary> {
    const url = `${this.baseUrl}/student/${studentId}/summary`;
    const response = await fetch(url, {
      method: 'GET',
      headers: this.getHeaders(),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || '상담 요약 조회에 실패했습니다.');
    }

    return response.json();
  }

  /**
   * 상담 기록 수정
   */
  async updateCounselingRecord(
    recordId: string,
    data: CounselingRecordUpdate
  ): Promise<CounselingRecord> {
    const response = await fetch(`${this.baseUrl}/${recordId}`, {
      method: 'PUT',
      headers: this.getHeaders(),
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || '상담 기록 수정에 실패했습니다.');
    }

    return response.json();
  }

  /**
   * AI 분석 결과 업데이트
   */
  async updateAIAnalysis(
    recordId: string,
    aiSummary: string,
    aiRecommendation: string
  ): Promise<CounselingRecord> {
    const response = await fetch(`${this.baseUrl}/${recordId}/ai-analysis`, {
      method: 'PUT',
      headers: this.getHeaders(),
      body: JSON.stringify({
        ai_summary: aiSummary,
        ai_recommendation: aiRecommendation,
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'AI 분석 업데이트에 실패했습니다.');
    }

    return response.json();
  }

  /**
   * 상담 기록 삭제
   */
  async deleteCounselingRecord(recordId: string): Promise<void> {
    const response = await fetch(`${this.baseUrl}/${recordId}`, {
      method: 'DELETE',
      headers: this.getHeaders(),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || '상담 기록 삭제에 실패했습니다.');
    }
  }

  /**
   * 상담 효과 조회
   */
  async getConsultationEffectiveness(recordId: string): Promise<{
    counseling_record_id: string;
    student_id: string;
    counseling_date: string;
    evaluation_date: string;
    pre_consultation_metrics: any;
    post_consultation_metrics: any;
    effectiveness_score: number;
  }> {
    const response = await fetch(`${this.baseUrl}/${recordId}/effectiveness`, {
      method: 'GET',
      headers: this.getHeaders(),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || '상담 효과 조회에 실패했습니다.');
    }

    return response.json();
  }

  /**
   * 상담 효과 평가 실행 (수동)
   */
  async evaluateConsultation(recordId: string): Promise<{
    counseling_record_id: string;
    student_id: string;
    counseling_date: string;
    evaluation_date: string;
    pre_consultation_metrics: any;
    post_consultation_metrics: any;
    effectiveness_score: number;
    evaluated_at: string;
  }> {
    const response = await fetch(`${this.baseUrl}/${recordId}/evaluate`, {
      method: 'POST',
      headers: this.getHeaders(),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || '상담 효과 평가에 실패했습니다.');
    }

    return response.json();
  }

  /**
   * 평가 대기 중인 상담 목록 조회
   */
  async getPendingEvaluations(): Promise<Array<{
    counseling_record_id: string;
    student_id: string;
    counselor_id: string;
    counseling_date: string;
    days_since_consultation: number;
  }>> {
    const response = await fetch(`${this.baseUrl}/pending-evaluations`, {
      method: 'GET',
      headers: this.getHeaders(),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || '평가 대기 목록 조회에 실패했습니다.');
    }

    return response.json();
  }

  /**
   * 조직의 상담 기록 조회 (별칭)
   * Alias for getOrganizationCounselingRecords
   */
  async getByOrganization(
    organizationId: string,
    limit: number = 20,
    offset: number = 0
  ): Promise<CounselingRecord[]> {
    return this.getOrganizationCounselingRecords(organizationId, limit, offset);
  }

  /**
   * 페이지네이션을 위한 전체 개수 계산
   */
  calculateTotalPages(totalCount: number, pageSize: number): number {
    return Math.ceil(totalCount / pageSize);
  }

  /**
   * 현재 페이지의 offset 계산
   */
  calculateOffset(page: number, pageSize: number): number {
    return (page - 1) * pageSize;
  }
}

export const counselingRecordService = new CounselingRecordService();
export default CounselingRecordService;
